<template lang="pug">
    div
        v-snackbar(
            v-model='snackbar.show'
            :color='snackbar.color'
            :timeout="snackbar.timeout"
            top
        )
            | {{ snackbar.text }}
            v-btn(dark text @click='snackbar.show = false')
                | Close
        div.ma-sm-2
            .text-center(v-if="isLoading")
                v-progress-circular(
                    :size='100'
                    :width='10'
                    color='#0f5b9a'
                    indeterminate
                    style="margin-top: 100px;"
                )

            v-container.mx-auto(v-if="!isLoading")
                .text-right(v-if="!createMode && !editableNote" style='margin-top: 30px; width: 100%')
                    v-btn(outlined color="green" small light @click="startCreate")
                        v-icon mdi-plus
                        | Add Note
                CreateEditNote(
                    v-if="createMode"
                    :handle-cancel-create="handleCancelCreate"
                    :handle-create-resource-note="handleCreateResourceNote"
                    :existing-note-names="existingNoteNames"
                )
                template(v-if="showNotes" v-for="note in resourceNotes")
                    CreateEditNote(
                        v-if="editableNote && editableNote.note_id === note.note_id"
                        :resource-note="editableNote"
                        :handle-cancel-edit="handleCancelEdit"
                        :handle-update-resource-note="handleUpdateResourceNote"
                        :existing-note-names="existingNoteNames"
                    )
                    resourceNoteDetails(
                        v-if="!editableNote || editableNote.note_id !== note.note_id"
                        :resource-note="note"
                        :key="note.id"
                        :start-edit="startEdit"
                        :format-date="formatDate"
                    )
                v-alert.mt-8(outlined type="warning" :icon="false" width="100%" v-else).text-center
                    | No notes yet
</template>

<script lang="ts">
import Vue from "vue";

// Data
import { GetCustomerResourceNotes, CreateResourceNote, UpdateResourceNote } from "@/data";
import ResourceNoteDetails from "@/components/resource_notes/resource_note_details.vue";
import CreateEditNote from "@/components/resource_notes/create_edit.vue";

export default Vue.extend({
    data() {
        return {
            existingNoteNames: [],
            resourceNotes: [],
            isLoading: true,
            snackbar: {
                show: false,
                timeout: 2500,
            },
            createMode: false,
            showDetails: false,
            editableNote: null,
        };
    },
    methods: {
        getResourceNotes() {
            if (!this.customerId) {
                this.isLoading = false;
                return;
            }
            
            this.isLoading = true;

            GetCustomerResourceNotes(this.customerId).then((response) => {
                if (response?.Data?.resource_notes) {
                    this.resourceNotes = response.Data.resource_notes;
                    this.existingNoteNames = response.Data.resource_notes.map((note) => note.name.toLowerCase());
                } else {
                    this.showSnackbar("error", "Something went wrong.");
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        showSnackbar(color, text) {
            if (color?.length > 0 && text?.length > 0) {
                this.snackbar.color = color;
                this.snackbar.text = text;
                this.snackbar.show = true;
            }
        },
        startEdit(note) {
            this.editableNote = note;
        },
        endEdit() {
            this.editableNote = null
        },  
        startCreate() {
            this.createMode = true;
        },
        handleCreateResourceNote(name = "", content = "") {
            const options = {
                account_id:     this.currentUser.account.account_id,
                content:        content,
                name:           name,
                organization_id: this.currentCampaignCustomer.organization_id,
                resource_id:    this.customerId,
                resource_type:  "customer", 
                user_id:        this.currentUser.user_id,
                user_name:      this.currentUser.full_name,
            };
            
            if (!(options?.name?.length > 0) || !(options?.content?.length > 0 || options?.resource_id > 0 || options?.user_id > 0)) {
                this.showSnackBar("red", "Failed to update customer note: Invalid content")
            }

            this.isLoading = true;
            
            CreateResourceNote(options).then((response) => {
                if (response?.Data?.ok) {
                    this.showSnackbar("green", "Successfully created customer note.");
                } else if (response.Error) {
                    this.showSnackbar("red", response.Error);
                } else {
                    this.showSnackbar("red", "Something went wrong.");
                }
                this.createMode = false;
                this.getResourceNotes();
            }).finally(() => {
                this.isLoading = false;
            });
        },
        handleUpdateResourceNote(resourceNote, name, content) {
            if (resourceNote === null || resourceNote.id === 0 || content?.length === 0) {
                this.showSnackbar("red", "Failed to create customer note: Invalid name or content")
                this.endEdit();
                return;
            }

            this.isLoading = true;

            const options = {
                account_id:         this.currentUser.account.account_id,
                content:            content,
                name:               name,
                organization_id:    this.currentCampaignCustomer.organization_id,
                resource_id:        resourceNote.resource_id,
                resource_type:      resourceNote.resource_type, 
                user_id:            this.currentUser.user_id,
                user_name:          this.currentUser.full_name,
            };

            UpdateResourceNote(resourceNote.note_id, options).then((response) => {
                if (response.Error) {
                    this.showSnackbar("red", response.Error)
                } else if (response.Data?.ok) {
                    this.showSnackbar("green", "Note edited successfully")
                }
                this.endEdit();
                this.getResourceNotes();
            }).finally(() => {
                this.isLoading = false;
            }) ;
        },
        handleCancelEdit() {
            this.endEdit();
        },
        handleCancelCreate() {
            this.createMode = false;
        },
    },
    computed: {
        archivedNotes() {
            return this.resourceNotes.slice(1);
        },
        anyArchivedNotes() {
            return this.archivedNotes.length > 0;
        },
        customerId() {
            return this.currentCampaignCustomer?.customer?.customer_id;
        },
        showNotes() {
            return !this.isLoading && this.resourceNotes?.length > 0;
        },
    },
    components: {
        ResourceNoteDetails,
        CreateEditNote,
    },
    props: {
        currentCampaignCustomer: Object,
        currentUser: {
            type: Object,
            required: true,
        },
    },
    created() {
        this.getResourceNotes();
    },
});
</script>

<style lang="scss" scoped>
    .edit-icon {
        cursor: pointer;
        font-size: 24px;
        color: #1867c0;
        transition: color 0.2s;
    }
    .edit-icon:hover {
        color: #0d47a1;
    }
</style>
